import { ErrorMessages } from "../../../../enums";
import { IdSchemaEnum } from "../../../Configurator/Enums/IdSchemaEnum";
import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import { ErrorCodes } from "../../../Exception/ErrorCodes";
import { ErrorStatuses } from "../../../Exception/ErrorStatuses";
import FansUnitedSdkException from "../../../Exception/FansUnitedSdkException";
import StandardFansUnitedException from "../../../Exception/StandardFansUnitedException";
import IdMappingService from "../../../IdMapping/IdMappingService";
import ActivityService from "../../Activity/Service/ActivityService";
import Football from "../../Football/Football";
import MiniGamesValidator from "../../MiniGames/Validator/MiniGamesValidator";
import PotmResultsModel from "../Models/PlayerOfTheMatch/PotmResultsModel";
import PotmUserVotesResponseModel from "../Models/PlayerOfTheMatch/PotmUserVotesResponseModel";
import PollsFilters from "../Models/Polls/Filters/PollsFilters";
import PollBasicModel from "../Models/Polls/PollBasicModel";

export default class VotingService {
  readonly idMappingService: IdMappingService = null;
  readonly idSchema: string = null;
  readonly errorHandlingMode: string = null;
  readonly validator: MiniGamesValidator = null;
  readonly activityService: ActivityService = null;
  private football: Football = null;

  constructor(config: SDKConfigurationModel) {
    this.idSchema = config.idSchema;
    this.errorHandlingMode = config.errorHandlingMode;
    this.idMappingService = new IdMappingService(config);
    this.validator = new MiniGamesValidator(config);
    this.activityService = new ActivityService(config);
    this.football = new Football(config);
  }

  public finalizePollsFilters = async (filters: PollsFilters) => {
    if (
      this.idSchema !== IdSchemaEnum.NATIVE &&
      filters.entityIds &&
      filters.entityIds.length > 0
    ) {
      this.validator.validateFilters(filters);
      const idsObj = await this.idMappingService.getEntityIdsBySchemaId(
        Array.from(filters.entityIds),
        filters.entityType,
        IdSchemaEnum.NATIVE
      );
      filters.entityIds = idsObj[filters.entityType];
    }
  };

  public remapTags = async (object: PollBasicModel, fromSchema, toSchema) => {
    if (
      this.idSchema != IdSchemaEnum.NATIVE &&
      object.context &&
      object.context.tags &&
      object.context.tags.length
    ) {
      const copyTags = JSON.parse(JSON.stringify(object.context.tags));
      const remapIdsTags = await this.activityService.remapTagsIds(
        copyTags,
        fromSchema,
        toSchema
      );
      //@ts-ignore
      object.context.tags = remapIdsTags;
    }
  };

  public remapMatchAndPlayerIds = async (
    matchId: string,
    playerId: string,
    fromSchema: string,
    toSchema: string
  ) => {
    const idsObj = { match: [matchId], player: [playerId] };
    const remappedIds =
      await this.idMappingService.idMappingFacade.getEntitiesByIds(
        idsObj,
        fromSchema,
        toSchema
      );

    if (remappedIds.match.length > 0 && remappedIds.player.length > 0) {
      const nativeMatchId = remappedIds.match[0];
      const nativePlayerId = remappedIds.player[0];
      return [nativeMatchId, nativePlayerId];
    }

    if (this.errorHandlingMode === "default") {
      throw new FansUnitedSdkException(
        ErrorCodes.NOT_FOUND,
        ErrorStatuses.ENTITY_IDS_NOT_FOUND,
        ErrorMessages.ENTITY_IDS_NOT_FOUND
      );
    }

    throw new StandardFansUnitedException(
      ErrorCodes.NOT_FOUND,
      ErrorStatuses.ENTITY_IDS_NOT_FOUND,
      ErrorMessages.ENTITY_IDS_NOT_FOUND
    );
  };

  public remapMatchIds = async (
    matchIds: string[],
    fromSchema: string,
    toSchema: string
  ) => {
    const originalMatchIds = Array.from(matchIds);
    const idsObj = { match: matchIds };
    const remappedIds =
      await this.idMappingService.idMappingFacade.getEntitiesByIds(
        idsObj,
        fromSchema,
        toSchema
      );

    if (
      remappedIds.match.length > 0 &&
      remappedIds.match.length === originalMatchIds.length
    ) {
      return remappedIds.match;
    }

    if (this.errorHandlingMode === "default") {
      throw new FansUnitedSdkException(
        ErrorCodes.NOT_FOUND,
        ErrorStatuses.MATCH_IDS_NOT_FOUND,
        ErrorMessages.MATCH_IDS_NOT_FOUND
      );
    }
    throw new StandardFansUnitedException(
      ErrorCodes.NOT_FOUND,
      ErrorStatuses.MATCH_IDS_NOT_FOUND,
      ErrorMessages.MATCH_IDS_NOT_FOUND
    );
  };

  public setModelsForUserVotes = async (
    response: PotmUserVotesResponseModel
  ) => {
    const matchIds = response.data.map((value) => value.matchId);
    const playerIds = response.data.map((value) => value.playerId);

    if (matchIds.length > 0 && playerIds.length > 0) {
      const matchesMap =
        await this.football.footballFacade.getMatchesMapWithNativeIds(matchIds);
      const playersMap =
        await this.football.footballFacade.getPlayersMapWithNativeIds(
          playerIds
        );
      response.data.forEach((value) => {
        value.matchModel = matchesMap[value.matchId] || null;
        value.playerModel = playersMap[value.playerId] || null;
        value.matchId = value.matchModel ? value.matchModel.id : value.matchId;
        value.playerId = value.playerModel
          ? value.playerModel.id
          : value.playerId;
      });

      if (response.meta.pagination.nextPageStartsAfter) {
        // Just in case we set the next page starts after, because it's a match ID and if the provider is not native
        // the client will need it to implement pagination
        response.meta.pagination.nextPageStartsAfter =
          response.data[response.data.length - 1].matchId;
      }
    }

    return response;
  };

  public setModelsForPotmResults = async (data: PotmResultsModel[]) => {
    const teamIds = data.map((value) => value.teamId);
    const playerIds = data.map((value) => value.playerId);

    if (teamIds.length > 0 && playerIds.length > 0) {
      const teamsMap =
        await this.football.footballFacade.getTeamsMapWithNativeIds(teamIds);
      const playersMap =
        await this.football.footballFacade.getPlayersMapWithNativeIds(
          playerIds
        );
      data.forEach((value) => {
        value.teamModel = teamsMap.get(value.teamId) || null;
        value.playerModel = playersMap[value.playerId] || null;
        value.teamId = value.teamModel ? value.teamModel.id : value.teamId;
        value.playerId = value.playerModel
          ? value.playerModel.id
          : value.playerId;
      });
    }
  };
}
